import axios from "axios";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// 画面内で使用するアイコン
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SaveIcon from "@mui/icons-material/Save";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import "../css/style.css";

import Header from "../Pages/Header";
import Loading from "./Loading";

import ConstList from "../utils/ConstList";
import { LoginUserAtom } from "../stores/LoginUserAtom";

/**
 * 管理者として扱う権限ID。
 *
 * master_authorityテーブルで、
 * authority_id = 1が管理者として登録されているため1を指定する。
 */
const ADMIN_AUTHORITY_ID = 1;

/**
 * ユーザー登録フォームの入力値。
 *
 * LoginUserTypeは変更せず、
 * この画面専用の型として定義する。
 */
type UserCreateForm = {
  name: string;
  email: string;
  authorityId: string;
};

/**
 * 入力項目ごとのエラーメッセージ。
 */
type UserCreateFormErrors = Partial<Record<keyof UserCreateForm, string>>;

/**
 * GetMyProfile.phpから返されるユーザー情報。
 *
 * ログイン中のユーザーが管理者かどうか確認するために使用する。
 */
type ProfileResponse = {
  success?: boolean;
  message?: string;
  data?: {
    user_id?: number | string;
    email?: string;
    name?: string;
    authority_id?: number | string;
    authority_name?: string;
  } | null;
};

/**
 * master_authorityに登録されている権限情報。
 */
type Authority = {
  authority_id: number | string;
  authority_name: string;
};

/**
 * GetAuthorities.phpから返される権限一覧。
 */
type AuthoritiesResponse = {
  success?: boolean;
  message?: string;
  data?: Authority[];
};

/**
 * CreateUser.phpから返される登録結果。
 */
type CreateUserResponse = {
  success?: boolean;
  message?: string;
  data?: {
    user_id?: number | string;
    email?: string;
    name?: string;
    authority_id?: number | string;
    authority_name?: string;
  };
};

/**
 * ユーザー追加画面。
 *
 * 処理の流れ：
 *
 * 1. LoginUserAtomからログインユーザーのメールアドレスを取得
 * 2. GetMyProfile.phpでログインユーザーの権限を確認
 * 3. 管理者の場合だけ登録フォームを表示
 * 4. GetAuthorities.phpから権限一覧を取得
 * 5. CreateUser.phpへ新規ユーザー情報を送信
 *
 * LoginUserTypeおよびLoginUserAtomの型は変更しない。
 */
const Setting_User = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  /**
   * Auth0ログイン後にRecoilへ保存されているユーザー情報。
   *
   * この画面ではemailだけを使用して、
   * DBに登録されている権限を別途取得する。
   */
  const loginUserData = useRecoilValue(LoginUserAtom);

  /**
   * ページの初期情報を取得しているかどうか。
   *
   * ログインユーザーの権限確認と、
   * 選択可能な権限一覧の取得が完了するまでtrue。
   */
  const [pageLoading, setPageLoading] = useState(true);

  /**
   * ページ初期化時のエラー。
   */
  const [pageError, setPageError] = useState<string | null>(null);

  /**
   * ログインユーザーの権限ID。
   *
   * LoginUserAtomには保存せず、
   * この画面のstateだけで管理する。
   */
  const [authorityId, setAuthorityId] = useState<number | null>(null);

  /**
   * ログインユーザーの権限名。
   *
   * 例：
   * 管理者
   */
  const [authorityName, setAuthorityName] = useState("");

  /**
   * ユーザー登録時に選択できる権限一覧。
   */
  const [authorities, setAuthorities] = useState<Authority[]>([]);

  /**
   * ユーザー登録フォームの入力値。
   */
  const [form, setForm] = useState<UserCreateForm>({
    name: "",
    email: "",
    authorityId: "",
  });

  /**
   * フォーム項目ごとのエラー。
   */
  const [formErrors, setFormErrors] = useState<UserCreateFormErrors>({});

  /**
   * ユーザー登録処理を実行中かどうか。
   */
  const [submitting, setSubmitting] = useState(false);

  /**
   * 登録成功時のメッセージ。
   */
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * 登録失敗時のメッセージ。
   */
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * authority_id = 1の場合だけ管理者として扱う。
   */
  const isAdmin = authorityId === ADMIN_AUTHORITY_ID;

  /**
   * この画面に必要な初期情報を取得する。
   *
   * 最初にログインユーザーの権限を確認する。
   * 管理者と確認できた場合だけ、権限一覧を取得する。
   */
  const loadPageData = useCallback(async () => {
    const loginEmail = loginUserData.email?.trim() ?? "";

    /**
     * LoginUserAtomへのユーザー情報設定が完了するまで待機する。
     */
    if (!loginEmail) {
      setPageLoading(true);
      return;
    }

    try {
      setPageLoading(true);
      setPageError(null);
      setAuthorityId(null);
      setAuthorityName("");
      setAuthorities([]);

      /**
       * ログインユーザーのDB上の権限を取得する。
       */
      const profileResponse = await axios.post<ProfileResponse>(
        ConstList.getApiUrl() + "GetMyProfile.php",
        {
          email: loginEmail,
        },
        ConstList.AXIOS_CONFIG,
      );

      const profile = profileResponse.data?.data;

      /**
       * DBにログインユーザーが存在しない場合。
       */
      if (!profile) {
        setPageError("ログインユーザーがシステムに登録されていません。");
        return;
      }

      const currentAuthorityId = Number(profile.authority_id ?? 0);

      const currentAuthorityName = profile.authority_name ?? "";

      setAuthorityId(currentAuthorityId);
      setAuthorityName(currentAuthorityName);

      /**
       * 管理者以外は権限一覧を取得せず、
       * 登録フォームも表示しない。
       */
      if (currentAuthorityId !== ADMIN_AUTHORITY_ID) {
        return;
      }

      /**
       * 新規ユーザーへ設定できる権限一覧を取得する。
       */
      const authoritiesResponse = await axios.get<AuthoritiesResponse>(
        ConstList.getApiUrl() + "GetAuthorities.php",
        ConstList.AXIOS_CONFIG,
      );

      const authorityList = authoritiesResponse.data?.data;

      if (!Array.isArray(authorityList)) {
        throw new Error("権限一覧の応答形式が正しくありません。");
      }

      setAuthorities(authorityList);

      /**
       * 選択可能な権限が1件だけの場合は、
       * 自動的にその権限を選択する。
       */
      if (authorityList.length === 1) {
        setForm((current) => ({
          ...current,
          authorityId: String(authorityList[0].authority_id),
        }));
      }
    } catch (caughtError) {
      if (axios.isAxiosError(caughtError)) {
        console.error("ユーザー追加画面の初期化に失敗しました。", {
          status: caughtError.response?.status,
          responseData: caughtError.response?.data,
          message: caughtError.message,
        });
      } else {
        console.error("ユーザー追加画面の初期化に失敗しました。", caughtError);
      }

      setPageError("ユーザー追加画面の情報を取得できませんでした。");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserData.email]);

  /**
   * 画面表示時に権限確認と権限一覧取得を実行する。
   *
   * LoginUserAtomのemailが後から設定された場合も再実行される。
   */
  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  /**
   * フォームの値を更新する。
   *
   * 入力された項目のエラーは、
   * 値を変更した時点で解除する。
   */
  const updateForm = (field: keyof UserCreateForm, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => ({
      ...current,
      [field]: undefined,
    }));

    setSuccessMessage(null);
    setSubmitError(null);
  };

  /**
   * ユーザー登録前の入力チェック。
   */
  const validateForm = () => {
    const nextErrors: UserCreateFormErrors = {};

    const name = form.name.trim();
    const email = form.email.trim();
    const selectedAuthorityId = form.authorityId.trim();

    /**
     * 氏名の必須・文字数チェック。
     */
    if (!name) {
      nextErrors.name = "氏名を入力してください。";
    } else if (name.length > 100) {
      nextErrors.name = "氏名は100文字以内で入力してください。";
    }

    /**
     * メールアドレスの必須・形式チェック。
     */
    if (!email) {
      nextErrors.email = "メールアドレスを入力してください。";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = "メールアドレスの形式が正しくありません。";
    } else if (email.length > 255) {
      nextErrors.email = "メールアドレスは255文字以内で入力してください。";
    }

    /**
     * 権限の必須チェック。
     */
    if (!selectedAuthorityId) {
      nextErrors.authorityId = "権限を選択してください。";
    }

    setFormErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  /**
   * 新規ユーザーを登録する。
   *
   * CreateUser.phpへ次の情報を送信する。
   *
   * ・name
   * ・email
   * ・authority_id
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSuccessMessage(null);
    setSubmitError(null);

    /**
     * 管理者以外はフロント側から登録処理を実行しない。
     */
    if (!isAdmin) {
      setSubmitError("ユーザーを登録する権限がありません。");
      return;
    }

    /**
     * 入力内容に問題がある場合はAPIを呼ばない。
     */
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post<CreateUserResponse>(
        ConstList.getApiUrl() + "CreateUser.php",
        {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          authority_id: Number(form.authorityId),
        },
        ConstList.AXIOS_CONFIG,
      );

      /**
       * HTTPステータスが200でも、
       * successがfalseの場合は登録失敗として扱う。
       */
      if (response.data?.success === false) {
        throw new Error(
          response.data.message || "ユーザー登録に失敗しました。",
        );
      }

      setSuccessMessage(response.data?.message || "ユーザーを登録しました。");

      /**
       * 登録成功後にフォームを初期化する。
       */
      setForm({
        name: "",
        email: "",
        authorityId:
          authorities.length === 1 ? String(authorities[0].authority_id) : "",
      });

      setFormErrors({});
    } catch (caughtError) {
      let message = "ユーザー登録に失敗しました。";

      if (axios.isAxiosError(caughtError)) {
        console.error("ユーザー登録に失敗しました。", {
          status: caughtError.response?.status,
          responseData: caughtError.response?.data,
          message: caughtError.message,
        });

        const apiMessage = caughtError.response?.data?.message;

        if (typeof apiMessage === "string") {
          message = apiMessage;
        }
      } else if (caughtError instanceof Error) {
        console.error("ユーザー登録に失敗しました。", caughtError);

        message = caughtError.message;
      }

      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 設定画面へ戻る。
   */
  const handleBack = () => {
    navigate("/setting");
  };

  return (
    <>
      {/* ログイン後の共通ヘッダー */}
      <Header />

      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          pt: {
            xs: 9,
            md: 10,
          },
          pb: 6,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(
                  180deg,
                  ${alpha(theme.palette.info.main, 0.16)} 0%,
                  ${theme.palette.background.default} 360px
                )`
              : `linear-gradient(
                  180deg,
                  ${alpha(theme.palette.info.main, 0.1)} 0%,
                  ${theme.palette.background.default} 360px
                )`,
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>
            {/* ==================================================
                ページヘッダー

                設定画面へ戻るボタン、ページ名、
                ログインユーザーの権限を表示する。
            ================================================== */}
            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 2.5,
                  md: 3.5,
                },
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.88),
                backdropFilter: "blur(12px)",
                boxShadow: `0 18px 45px ${alpha(
                  theme.palette.common.black,
                  0.08,
                )}`,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Button
                    type="button"
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{
                      px: 0,
                      minWidth: 0,
                      fontWeight: 700,
                    }}
                  >
                    設定へ戻る
                  </Button>
                </Box>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  spacing={2}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 3,
                        color: "info.main",
                        backgroundColor: alpha(theme.palette.info.main, 0.12),
                      }}
                    >
                      <PersonAddAlt1Icon />
                    </Box>

                    <Box>
                      <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        ユーザー追加
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.5,
                          color: "text.secondary",
                          fontSize: {
                            xs: 13,
                            md: 14,
                          },
                        }}
                      >
                        システムを利用するユーザーを 新しく登録します。
                      </Typography>
                    </Box>
                  </Stack>

                  {!pageLoading && authorityName && (
                    <Chip
                      icon={<AdminPanelSettingsIcon />}
                      label={`ログイン権限：${authorityName}`}
                      color={isAdmin ? "success" : "default"}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Paper>

            {/* ==================================================
                初期情報取得中
            ================================================== */}
            {pageLoading ? (
              <Paper
                elevation={0}
                sx={{
                  minHeight: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              >
                <Loading />
              </Paper>
            ) : pageError ? (
              /* 初期情報の取得に失敗した場合 */
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button
                    type="button"
                    color="error"
                    size="small"
                    onClick={() => {
                      void loadPageData();
                    }}
                  >
                    再試行
                  </Button>
                }
                sx={{
                  borderRadius: 3,
                }}
              >
                {pageError}
              </Alert>
            ) : !isAdmin ? (
              /* 管理者以外がURLを直接開いた場合 */
              <Alert
                severity="warning"
                variant="outlined"
                action={
                  <Button
                    type="button"
                    color="warning"
                    size="small"
                    onClick={handleBack}
                  >
                    設定へ戻る
                  </Button>
                }
                sx={{
                  borderRadius: 3,
                }}
              >
                この画面を利用する権限がありません。
              </Alert>
            ) : (
              /* 管理者の場合だけ登録フォームを表示する */
              <Paper
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 16px 40px ${alpha(
                    theme.palette.common.black,
                    0.06,
                  )}`,
                }}
              >
                {/* 登録フォームの見出し */}
                <Box
                  sx={{
                    px: {
                      xs: 2.5,
                      md: 3,
                    },
                    py: 2.25,
                    backgroundColor: alpha(theme.palette.info.main, 0.06),
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    ユーザー情報
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.5,
                      color: "text.secondary",
                      fontSize: 13,
                    }}
                  >
                    氏名、メールアドレス、 システム内の権限を入力してください。
                  </Typography>
                </Box>

                <Divider />

                {/* 登録成功・失敗メッセージ */}
                <Box
                  sx={{
                    px: {
                      xs: 2.5,
                      md: 3,
                    },
                    pt: 3,
                  }}
                >
                  {successMessage && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 2,
                        borderRadius: 3,
                      }}
                    >
                      {successMessage}
                    </Alert>
                  )}

                  {submitError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        borderRadius: 3,
                      }}
                    >
                      {submitError}
                    </Alert>
                  )}
                </Box>

                {/* ユーザー登録フォーム */}
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{
                    p: {
                      xs: 2.5,
                      md: 3,
                    },
                    pt: 1,
                  }}
                >
                  <Stack spacing={3}>
                    {/* 氏名入力 */}
                    <TextField
                      required
                      fullWidth
                      label="氏名"
                      placeholder="例：佐藤 麗乃"
                      value={form.name}
                      disabled={submitting}
                      error={Boolean(formErrors.name)}
                      helperText={
                        formErrors.name ||
                        "システム内に表示する氏名を入力します。"
                      }
                      slotProps={{
                        htmlInput: {
                          maxLength: 100,
                        },
                      }}
                      onChange={(event) => {
                        updateForm("name", event.target.value);
                      }}
                    />

                    {/* メールアドレス入力 */}
                    <TextField
                      required
                      fullWidth
                      type="email"
                      label="メールアドレス"
                      placeholder="例：user@example.com"
                      value={form.email}
                      disabled={submitting}
                      error={Boolean(formErrors.email)}
                      helperText={
                        formErrors.email ||
                        "Auth0へ登録するメールアドレスと同じ値を使用します。"
                      }
                      slotProps={{
                        htmlInput: {
                          maxLength: 255,
                        },
                      }}
                      onChange={(event) => {
                        updateForm("email", event.target.value);
                      }}
                    />

                    {/* 権限選択 */}
                    <TextField
                      select
                      required
                      fullWidth
                      label="権限"
                      value={form.authorityId}
                      disabled={submitting || authorities.length === 0}
                      error={Boolean(formErrors.authorityId)}
                      helperText={
                        formErrors.authorityId ||
                        "このユーザーに付与する権限を選択します。"
                      }
                      onChange={(event) => {
                        updateForm("authorityId", event.target.value);
                      }}
                    >
                      <MenuItem value="">選択してください</MenuItem>

                      {authorities.map((authority) => (
                        <MenuItem
                          key={String(authority.authority_id)}
                          value={String(authority.authority_id)}
                        >
                          {authority.authority_name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* 操作ボタン */}
                    <Stack
                      direction={{
                        xs: "column-reverse",
                        sm: "row",
                      }}
                      justifyContent="flex-end"
                      spacing={1.5}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleBack}
                        disabled={submitting}
                        sx={{
                          minWidth: 120,
                          borderRadius: 2,
                          fontWeight: 700,
                        }}
                      >
                        キャンセル
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        color="info"
                        startIcon={
                          submitting ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        disabled={submitting || authorities.length === 0}
                        sx={{
                          minWidth: 160,
                          borderRadius: 2,
                          px: 3,
                          py: 1.1,
                          fontWeight: 700,
                          boxShadow: "none",
                        }}
                      >
                        {submitting ? "登録中" : "ユーザーを登録"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Setting_User;
